// src/components/IconOptionSelector.js
import React from "react"

function IconOptionSelector({
  title,
  options,
  selectedValue,
  onSelect,
  itemType,
  onOptionHover,
}) {
  const handleMouseEnter = (option) => {
    if (onOptionHover) {
      onOptionHover({
        label: option.label,
        description:
          option.description ||
          `The ${
            option.label
          } style. Choose this to define your dress's ${title.toLowerCase()}.`,
        icon: option.icon,
        swatch: option.swatch,
        border: option.border,
        itemType: itemType,
      });
    }
  };

  const handleMouseLeave = () => {
    if (onOptionHover) {
      onOptionHover(null);
    }
  };

  if (!options || options.length === 0) {
    return (
      <div className="mb-4 p-3 py-5 bg-gray-100/70 rounded-lg text-center">
        <h3 className="text-md font-semibold text-gray-600 mb-1 pl-1">
          {title}
        </h3>
        <p className="text-sm text-gray-400 italic pl-1">
          No compatible options for current selections.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mb-4"
      onMouseLeave={itemType !== "color" ? handleMouseLeave : undefined}
    >
      <h3 className="text-md font-semibold text-gray-700 mb-2.5 pl-2">
        {title}
      </h3>
      <div
        className={`grid gap-2 sm:gap-2.5 ${
          // Adjusted gap for consistency
          itemType === "color"
            ? "grid-cols-5 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8" // More columns for small color swatches
            : itemType === "fabric"
            ? "grid-cols-3 sm:grid-cols-3 md:grid-cols-4" // Fabric swatches can be a bit larger
            : "grid-cols-3 sm:grid-cols-3 md:grid-cols-3" // General icons: start with 3, can adjust if needed
        }`}
      >
        {options.map((option) => {
          const isSelected = selectedValue === option.value;
          let content;
          // Base classes for the button, ensuring it can grow but has a reasonable base size
          let commonButtonClasses =
            "p-2 rounded-lg transition-all duration-200 text-center w-full flex flex-col items-center justify-start focus:outline-none cursor-pointer aspect-[4/5] sm:aspect-square";
          let activeClasses = isSelected
            ? "ring-2 ring-indigo-500 shadow-lg bg-indigo-100/80"
            : "bg-white hover:bg-gray-50 hover:shadow-md border border-gray-200";

          if (itemType === "color" && option.swatch) {
            content = (
              <div
                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full shadow-inner ${
                  option.border ? "border-2 border-gray-300" : ""
                }`}
                style={{ backgroundColor: option.swatch }}
              ></div>
            );
            activeClasses = isSelected
              ? `ring-2 ring-offset-2 ring-indigo-500 shadow-md`
              : `hover:ring-1 hover:ring-offset-1 hover:ring-gray-400`;
            commonButtonClasses =
              "p-1 rounded-full transition-all duration-200 flex items-center justify-center focus:outline-none w-auto aspect-square"; // Make color swatches smaller and consistently round
          } else if (itemType === "fabric" && option.icon) {
            content = (
              <img
                src={option.icon}
                alt={option.label}
                className="w-full h-full object-cover rounded-md shadow-sm"
              />
            );
            commonButtonClasses =
              "p-0 rounded-lg transition-all duration-200 text-center w-full flex flex-col items-center justify-center aspect-square overflow-hidden"; // Fabric swatches can be square
            activeClasses = isSelected
              ? "ring-2 ring-indigo-500 shadow-lg opacity-100"
              : "bg-gray-50 hover:opacity-80 border border-gray-200";
          } else if (option.icon) {
            content = (
              <img
                src={option.icon}
                alt={option.label}
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain mx-auto"
              />
            ); // Icon size
          }

          return (
            <div
              key={option.value}
              className="flex flex-col items-center"
              onMouseEnter={
                itemType === "color"
                  ? () => handleMouseEnter(option)
                  : undefined
              }
              onMouseLeave={itemType === "color" ? handleMouseLeave : undefined}
            >
              <button
                onClick={() => onSelect(option.value)}
                title={option.label}
                className={`${commonButtonClasses} ${activeClasses}`}
                onMouseEnter={
                  itemType !== "color"
                    ? () => handleMouseEnter(option)
                    : undefined
                }
              >
                {content}
              </button>
              {/* Adjusted text size for better readability on small screens */}
              <p
                className={`text-[0.65rem] leading-tight sm:text-xs font-medium mt-1 text-center truncate w-full px-0.5 ${
                  isSelected ? "text-indigo-700 font-semibold" : "text-gray-600"
                }`}
              >
                {option.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
export default IconOptionSelector;
